<template lang="pug">
  div
    .mb-6
      h1.display-1.mb-2 Card Component
      p.text--secondary A flexible container component for grouping related content with customizable border radius.

    // Card Examples
    .component-section.mb-8#basic-cards
      h3.mb-4.cursor-pointer(@click="navigateToSection('basic-cards')") Basic Card Examples
      .variations-grid
        .variation-item.mb-6(v-for="variation in cardVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .mb-2
              span.font-weight-medium {{ variation.label }}
              small.text--secondary.ml-2(v-if="variation.hint") {{ variation.hint }}
            
            // Card Implementation
            yz-card(v-bind="variation.props" style="max-width: 400px; margin: 0 auto;")
              template(v-if="variation.hasTitle")
                yz-card-title {{ variation.title }}
              template(v-if="variation.hasSubtitle")
                yz-card-subtitle {{ variation.subtitle }}
              template(v-if="variation.hasText")
                yz-card-text {{ variation.text }}
              template(v-if="variation.hasActions")
                yz-card-actions.d-flex.justify-end.gap-2
                  yz-btn(v-for="action in variation.actions" :key="action.label" :label="action.label" v-bind="action.props")

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // Radius Examples
    .component-section.mb-8#radius-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('radius-examples')") Radius Examples
      .variations-grid
        .variation-item.mb-6(v-for="variation in radiusVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .mb-2
              span.font-weight-medium {{ variation.label }}
              small.text--secondary.ml-2(v-if="variation.hint") {{ variation.hint }}
            
            // Card Implementation
            yz-card(v-bind="variation.props" style="max-width: 400px; margin: 0 auto;")
              template(v-if="variation.hasTitle")
                yz-card-title {{ variation.title }}
              template(v-if="variation.hasSubtitle")
                yz-card-subtitle {{ variation.subtitle }}
              template(v-if="variation.hasText")
                yz-card-text {{ variation.text }}
              template(v-if="variation.hasActions")
                yz-card-actions.d-flex.justify-end.gap-2
                  yz-btn(v-for="action in variation.actions" :key="action.label" :label="action.label" v-bind="action.props")

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // Color Examples
    .component-section.mb-8#color-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('color-examples')") Color Examples
      .variations-grid
        .variation-item.mb-6(v-for="variation in colorVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .mb-2
              span.font-weight-medium {{ variation.label }}
              small.text--secondary.ml-2(v-if="variation.hint") {{ variation.hint }}
            
            // Card Implementation
            yz-card(v-bind="variation.props" style="max-width: 400px; margin: 0 auto;")
              template(v-if="variation.hasTitle")
                yz-card-title {{ variation.title }}
              template(v-if="variation.hasSubtitle")
                yz-card-subtitle {{ variation.subtitle }}
              template(v-if="variation.hasText")
                yz-card-text {{ variation.text }}
              template(v-if="variation.hasActions")
                yz-card-actions.d-flex.justify-end.gap-2
                  yz-btn(v-for="action in variation.actions" :key="action.label" :label="action.label" v-bind="action.props")

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // API Documentation
    .component-section.mb-8#api
      h3.mb-4.cursor-pointer(@click="navigateToSection('api')") API
      
      h4.mb-3 Props
      .props-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Prop
                th Type
                th Default
                th Description
            tbody
              tr(v-for="prop in cardProps" :key="prop.name")
                td.font-weight-medium.text-no-wrap {{ prop.name }}
                td.text--secondary.text-no-wrap {{ prop.type }}
                td.text--secondary.text-no-wrap {{ prop.default }}
                td.single-line {{ prop.description }}
      
      h4.mb-3 Slots
      .slots-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Slot
                th Description
            tbody
              tr(v-for="slot in cardSlots" :key="slot.name")
                td.font-weight-medium.text-no-wrap {{ slot.name }}
                td.single-line {{ slot.description }}

      h4.mb-3 Related Components
      .related-components
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Component
                th Description
            tbody
              tr(v-for="component in relatedComponents" :key="component.name")
                td.font-weight-medium.text-no-wrap {{ component.name }}
                td.single-line {{ component.description }}
</template>

<script>
export default {
  data() {
    return {
      copiedCode: null
    }
  },
  computed: {
    cardVariations() {
      return [
        {
          name: 'Basic Card',
          id: 'basic-card-example',
          description: 'Simple card with title and text content',
          label: 'Basic Card',
          hint: 'Default 10px radius',
          props: {},
          hasTitle: true,
          hasText: true,
          title: 'Card Title',
          text: 'This is a basic card with some text content. Cards are flexible containers for grouping related information.',
          code: `yz-card
  yz-card-title Card Title
  yz-card-text This is a basic card with some text content. Cards are flexible containers for grouping related information.`
        },
        {
          name: 'Outlined Card',
          id: 'outlined-card-example',
          description: 'Card with outlined styling',
          label: 'Outlined Card',
          hint: 'Border only with transparent background',
          props: { outlined: true },
          hasTitle: true,
          hasText: true,
          title: 'Outlined Card',
          text: 'This card uses the outlined variant for a cleaner, border-only appearance without background fill.',
          code: `yz-card(outlined)
  yz-card-title Outlined Card
  yz-card-text This card uses the outlined variant for a cleaner, border-only appearance without background fill.`
        },
        {
          name: 'Card with Subtitle',
          id: 'subtitle-card-example',
          description: 'Card with title, subtitle, and text',
          label: 'Card with Subtitle',
          hint: 'Includes subtitle section',
          props: { outlined: true },
          hasTitle: true,
          hasSubtitle: true,
          hasText: true,
          title: 'Main Title',
          subtitle: 'Subtitle information',
          text: 'This card demonstrates the use of both title and subtitle sections to organize content hierarchy.',
          code: `yz-card(outlined)
  yz-card-title Main Title
  yz-card-subtitle Subtitle information
  yz-card-text This card demonstrates the use of both title and subtitle sections to organize content hierarchy.`
        },
        {
          name: 'Card with Actions',
          id: 'actions-card-example',
          description: 'Card with action buttons at the bottom',
          label: 'Card with Actions',
          hint: 'Includes action buttons',
          props: { outlined: true },
          hasTitle: true,
          hasText: true,
          hasActions: true,
          title: 'Action Card',
          text: 'This card includes action buttons that allow users to interact with the content.',
          actions: [
            { label: 'Cancel', props: { outlined: true, size: 'md' } },
            { label: 'Confirm', props: { color: 'primary', size: 'md' } }
          ],
          code: `yz-card(outlined)
  yz-card-title Action Card
  yz-card-text This card includes action buttons that allow users to interact with the content.
  yz-card-actions.d-flex.justify-end.gap-2
    yz-btn(outlined size="md" label="Cancel")
    yz-btn(color="primary" size="md" label="Confirm")`
        },
        {
          name: 'Full Example',
          id: 'full-example',
          description: 'Complete card with all elements',
          label: 'Complete Card',
          hint: 'Title, subtitle, text, and actions',
          props: { radius: '12px', outlined: true },
          hasTitle: true,
          hasSubtitle: true,
          hasText: true,
          hasActions: true,
          title: 'Complete Card Example',
          subtitle: 'Demonstrating all card features',
          text: 'This card showcases all available card elements including title, subtitle, text content, and action buttons with a custom radius.',
          actions: [
            { label: 'Learn More', props: { outlined: true, size: 'md' } },
            { label: 'Get Started', props: { color: 'primary', size: 'md' } }
          ],
          code: `yz-card(radius="12px" outlined)
  yz-card-title Complete Card Example
  yz-card-subtitle Demonstrating all card features
  yz-card-text This card showcases all available card elements including title, subtitle, text content, and action buttons with a custom radius.
  yz-card-actions.d-flex.justify-end.gap-2
    yz-btn(outlined size="md" label="Learn More")
    yz-btn(color="primary" size="md" label="Get Started")`
        }
      ]
    },
    radiusVariations() {
      return [
        {
          name: 'Small Radius (4px)',
          id: 'small-radius-example',
          description: 'Card with small border radius',
          label: 'Small Radius (4px)',
          hint: 'radius="4px"',
          props: { radius: '4px', outlined: true },
          hasTitle: true,
          hasText: true,
          title: 'Small Radius',
          text: 'This card has a smaller border radius of 4px for a more subtle rounded appearance.',
          code: `yz-card(radius="4px" outlined)
  yz-card-title Small Radius
  yz-card-text This card has a smaller border radius of 4px for a more subtle rounded appearance.`
        },
        {
          name: 'Default Radius (10px)',
          id: 'default-radius-example',
          description: 'Card with default border radius',
          label: 'Default Radius (10px)',
          hint: 'Default value',
          props: { outlined: true },
          hasTitle: true,
          hasText: true,
          title: 'Default Radius',
          text: 'This card uses the default border radius of 10px.',
          code: `yz-card(outlined)
  yz-card-title Default Radius
  yz-card-text This card uses the default border radius of 10px.`
        },
        {
          name: 'Large Radius (20px)',
          id: 'large-radius-example',
          description: 'Card with large border radius',
          label: 'Large Radius (20px)',
          hint: 'radius="20px"',
          props: { radius: '20px', outlined: true },
          hasTitle: true,
          hasText: true,
          title: 'Large Radius',
          text: 'This card has a larger border radius of 20px for a more pronounced rounded appearance.',
          code: `yz-card(radius="20px" outlined)
  yz-card-title Large Radius
  yz-card-text This card has a larger border radius of 20px for a more pronounced rounded appearance.`
        },
        {
          name: 'Numeric Radius (16)',
          id: 'number-radius-example',
          description: 'Card with numeric radius value',
          label: 'Numeric Radius (16)',
          hint: ':radius="16"',
          props: { radius: 16, outlined: true },
          hasTitle: true,
          hasText: true,
          title: 'Numeric Radius',
          text: 'This card uses a numeric radius value of 16, which automatically gets converted to 16px.',
          code: `yz-card(:radius="16" outlined)
  yz-card-title Numeric Radius
  yz-card-text This card uses a numeric radius value of 16, which automatically gets converted to 16px.`
        },
        {
          name: 'No Radius (0)',
          id: 'no-radius-example',
          description: 'Card with no border radius',
          label: 'No Radius',
          hint: 'radius="0"',
          props: { radius: '0', outlined: true },
          hasTitle: true,
          hasText: true,
          title: 'Sharp Corners',
          text: 'This card has no border radius, creating sharp corners for a more geometric appearance.',
          code: `yz-card(radius="0" outlined)
  yz-card-title Sharp Corners
  yz-card-text This card has no border radius, creating sharp corners for a more geometric appearance.`
        }
      ]
    },
    colorVariations() {
      return [
        {
          name: 'Background Color',
          id: 'colored-card-example',
          description: 'Card with custom background color',
          label: 'Background Color',
          hint: 'color="blue-100"',
          props: { color: 'blue-100', outlined: true },
          hasTitle: true,
          hasText: true,
          title: 'Colored Background',
          text: 'This card demonstrates how to use custom background colors with design system tokens.',
          code: `yz-card(color="blue-100" outlined)
  yz-card-title Colored Background
  yz-card-text This card demonstrates how to use custom background colors with design system tokens.`
        },
        {
          name: 'Border Color',
          id: 'border-color-example',
          description: 'Card with custom border color',
          label: 'Border Color',
          hint: 'border-color="danger-500"',
          props: { borderColor: 'danger-500', outlined: true },
          hasTitle: true,
          hasText: true,
          title: 'Custom Border',
          text: 'This card shows how to customize the border color for outlined cards.',
          code: `yz-card(border-color="danger-500" outlined)
  yz-card-title Custom Border
  yz-card-text This card shows how to customize the border color for outlined cards.`
        },
        {
          name: 'Hex Color',
          id: 'hex-color-example',
          description: 'Card with hex color value',
          label: 'Hex Color',
          hint: 'color="#e3f2fd"',
          props: { color: '#e3f2fd', outlined: true },
          hasTitle: true,
          hasText: true,
          title: 'Hex Color',
          text: 'This card uses a hex color value for the background.',
          code: `yz-card(color="#e3f2fd" outlined)
  yz-card-title Hex Color
  yz-card-text This card uses a hex color value for the background.`
        },
        {
          name: 'Combined Colors',
          id: 'combined-colors-example',
          description: 'Card with both background and border colors',
          label: 'Combined Colors',
          hint: 'Both color props',
          props: { color: 'red-100', borderColor: 'red-500', outlined: true },
          hasTitle: true,
          hasText: true,
          title: 'Combined Colors',
          text: 'This card combines custom background and border colors for a cohesive design.',
          code: `yz-card(color="red-100" border-color="red-500" outlined)
  yz-card-title Combined Colors
  yz-card-text This card combines custom background and border colors for a cohesive design.`
        },
        {
          name: 'Custom Border Width',
          id: 'border-width-example',
          description: 'Card with custom border width',
          label: 'Border Width',
          hint: 'border-width="3"',
          props: { borderWidth: 3, borderColor: 'primary-500', outlined: true },
          hasTitle: true,
          hasText: true,
          title: 'Thick Border',
          text: 'This card demonstrates custom border width with a thicker 3px border.',
          code: `yz-card(border-width="3" border-color="primary-500" outlined)
  yz-card-title Thick Border
  yz-card-text This card demonstrates custom border width with a thicker 3px border.`
        }
      ]
    },
    cardProps() {
      return [
        {
          name: 'radius',
          type: 'String | Number',
          default: '10px',
          description: 'Sets the border radius of the card. Numbers are automatically converted to px values.'
        },
        {
          name: 'outlined',
          type: 'Boolean',
          default: 'false',
          description: 'Applies outlined styling with border only and transparent background.'
        },
        {
          name: 'color',
          type: 'String',
          default: 'null',
          description: 'Sets the background color of the card. Accepts hex colors (#ff0000), CSS variables (var(--color)), or design system tokens (primary-500).'
        },
        {
          name: 'borderColor',
          type: 'String',
          default: 'yz-neutral-300',
          description: 'Sets the border color of the card. Accepts hex colors (#ff0000), CSS variables (var(--color)), or design system tokens (primary-500).'
        },
        {
          name: 'borderWidth',
          type: 'String | Number',
          default: '1',
          description: 'Sets the border width of the card. Numbers are automatically converted to px values.'
        },
        {
          name: 'borderStyle',
          type: 'String',
          default: 'solid',
          description: 'Sets the border style of the card. Accepts "solid", "dashed", or "dotted".'
        }
      ]
    },
    cardSlots() {
      return [
        {
          name: 'default',
          description: 'Main content area of the card. Use with yz-card-title, yz-card-subtitle, yz-card-text, and yz-card-actions components.'
        }
      ]
    },
    relatedComponents() {
      return [
        {
          name: 'yz-card-title',
          description: 'Primary title section for the card content'
        },
        {
          name: 'yz-card-subtitle',
          description: 'Secondary subtitle section for additional context'
        },
        {
          name: 'yz-card-text',
          description: 'Main text content area for card body content'
        },
        {
          name: 'yz-card-actions',
          description: 'Action button container, typically placed at the bottom of the card'
        }
      ]
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollToSection()
    })
  },
  watch: {
    '$route.hash': {
      handler() {
        this.scrollToSection()
      },
      immediate: false
    }
  },
  methods: {
    navigateToSection(sectionId) {
      this.$router.push({
        name: this.$route.name,
        hash: `#${sectionId}`,
        query: this.$route.query
      })
    },
    scrollToSection() {
      const sectionIdWithHash = this.$route.hash
      const sectionId = sectionIdWithHash.replace('#', '')
      if (sectionId) {
        this.$nextTick(() => {
          const element = document.getElementById(sectionId)
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            })
          }
        })
      }
    },
    async copyCode(code) {
      try {
        await navigator.clipboard.writeText(code)
        this.copiedCode = code
        setTimeout(() => {
          this.copiedCode = null
        }, 2000)
      } catch (err) {
        console.error('Failed to copy code:', err)
      }
    }
  }
}
</script>

<style scoped>
.variations-grid {
  max-width: 100%;
}

.variation-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background-color: #ffffff;
}

.variation-preview {
  background-color: #fafafa;
  border: 1px dashed #d0d0d0 !important;
}

.code-block {
  background-color: #f8f9fa !important;
  border: 1px solid #e9ecef;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.5;
}

.code-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.component-section {
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 24px;
}

.props-table .v-data-table {
  border: 1px solid #e0e0e0;
}

.single-line {
  white-space: normal;
  word-wrap: break-word;
  max-width: 400px;
}

.text-no-wrap {
  white-space: nowrap;
}

.cursor-pointer {
  cursor: pointer;
  transition: color 0.2s ease;
}

.cursor-pointer:hover {
  color: #1976d2;
}
</style>